
import { defineComponent, PropType, computed, reactive, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { Checkbox } from '@/types/InputItems'
import Alert from '@/components/elements/Alert.vue'
import Button from '@/components/elements/Button.vue'
import FavoriteButton from '@/components/elements/FavoriteButton.vue'
import HeadingLv2 from '@/components/elements/HeadingLv2.vue'
import FormAreaCheckbox from '@/components/block/FormAreaCheckbox.vue'
import HospitalCassetteOnlineEvent from '@/components/block/HospitalCassetteOnlineEvent.vue'
import ModalOnlineEvent from '@/components/block/ModalOnlineEvent.vue'
import ModalEventNotEntry from '@/components/block/ModalEventNotEntry.vue'
import EventDetailOnlineTable from '@/components/block/EventDetailOnlineTable.vue'
import ModalOnlineEventConfirm from '@/components/block/ModalOnlineEventConfirm.vue'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import 'dayjs/locale/ja'
dayjs.locale('ja')
dayjs.extend(isBetween)
import { axiosSetting } from '@/utils/axiosSetting'
import {
  EventDetailOnlineChannel,
  EventDetailOnlineProgram
} from '@/types/EventDetail'
import { getUniqeStringArray } from '@/utils/eventDetailUtils'

interface EventProgram {
  channel_name: string
  channel_description: string
  item: EventDetailOnlineProgram[]
}

export default defineComponent({
  name: 'EventDetailOnlineLive',

  components: {
    Alert,
    Button,
    FavoriteButton,
    HeadingLv2,
    FormAreaCheckbox,
    HospitalCassetteOnlineEvent,
    ModalOnlineEvent,
    ModalEventNotEntry,
    EventDetailOnlineTable,
    ModalOnlineEventConfirm
  },

  props: {
    hospitals: {
      type: Object,
      default: () => ({})
    },
    event: {
      type: Object,
      default: () => ({})
    },
    isSubscribed: {
      type: Boolean,
      required: true
    },
    isNoEntry: {
      type: Boolean,
      required: false
    },
    getDateToWeek: {
      type: Function as PropType<(date: string) => void>,
      required: true
    },
    eventDate: {
      type: String,
      required: true
    }
  },

  setup(props) {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()
    const state = reactive({
      isFilteredFavorite: false,
      isRequestMode: false,
      isCheckInError: false,
      confirmHospitals: []
    })

    const hasEventPrograms = computed(() => !!props.event.programs.length)
    const hasHospitals = computed(
      () =>
        'hospital' in props.hospitals &&
        !!Object.keys(props.hospitals.hospital).length
    )

    const eventDetailOnlineChannels = computed(() => {
      const channels: EventDetailOnlineChannel[] = []
      props.event.programs.map((program: EventProgram) => {
        const channel = {
          channel_name: program.channel_name,
          channel_description: program.channel_description,
          programs: program.item
        }
        channels.push(channel)
      })
      return channels
    })

    /*
     * 現在時間の制御系処理
     */

    // 現在時刻を取得
    const getCurrentTime = (): string => {
      const d = new Date()
      const year = d.getFullYear()
      const month = d.getMonth() + 1
      const day = d.getDate()
      const hour = d.getHours()
      const minutes = ('00' + d.getMinutes()).slice(-2)
      return `${year}-${month}-${day} ${hour}:${minutes}`
    }
    const currentTime = ref(getCurrentTime())

    // 現在時刻をアップデート
    const updateCurrentTime = (): void => {
      if (document.visibilityState === 'visible') {
        currentTime.value = getCurrentTime()
      }
    }

    // 現在時刻を10秒ごとに更新する
    const timerToGetCurrentTime = window.setInterval(updateCurrentTime, 10000)

    // ページ遷移時にタイマーリセット
    onBeforeRouteLeave((): void => {
      store.commit('unFixBody')
      window.clearInterval(timerToGetCurrentTime)
    })

    /*
     * 検索条件
     */
    const filterConditions: any = reactive({})

    const initializeFilterConditions = () => {
      if (props.hospitals.area) {
        filterConditions.area = {
          title: 'エリア',
          items: props.hospitals.area.map((area: string, i: number) => {
            return {
              id: `area${i}`,
              label: area,
              value: area,
              checked: false
            }
          })
        }
      }
      if (props.hospitals.hospital_class) {
        filterConditions.hc = {
          title: '種別',
          items: Object.keys(props.hospitals.hospital_class).map(
            (key: string, i: number) => {
              return { id: `hc${i}`, label: key, value: key, checked: false }
            }
          )
        }
      }
      if (props.hospitals.discerning_conditions) {
        filterConditions.dc = {
          title: 'キーワード',
          items: Object.keys(props.hospitals.discerning_conditions).map(
            (key: string, i: number) => {
              return { id: `dc${i}`, label: key, value: key, checked: false }
            }
          )
        }
      }
    }

    watch(
      () => props.hospitals,
      () => initializeFilterConditions()
    )

    const changeFilterConditions = (
      type: string,
      e: { value: string; checked: boolean }
    ) => {
      if (e.checked && state.isFilteredFavorite)
        state.isFilteredFavorite = false
      filterConditions[type].items.forEach((item: Checkbox) => {
        if (item.label === e.value) item.checked = e.checked
      })
    }

    const getLiveStatus = (
      startTimes: string[],
      nowStr: string,
      endTime: string[]
    ): {
      status: string
      programStartTime: string | null
    } => {
      console.log('startTimes', startTimes)
      const eventYYYYMMDD = props.event.term_start.split(' ')[0]
      const now = dayjs(nowStr)
      let status = ''
      let programStartTime: string | null = null

      startTimes.forEach((startTime: string) => {
        if (['live', 'soon', 'before'].includes(status)) return
        const pStart = dayjs(`${eventYYYYMMDD} ${startTime}`)
        console.log('pEnd', endTime)
        const pEnd =
          endTime.length > 0
            ? dayjs(`${eventYYYYMMDD} ${endTime}`)
            : pStart.add(30, 'minute')

        if (now.isSame(pStart)) {
          status = 'live'
          programStartTime = startTime
          return
        }
        if (now.isBetween(pStart, pEnd)) {
          status = 'live'
          programStartTime = startTime
          return
        }
        if (now.isBefore(pStart)) {
          status = 'before'
          programStartTime = startTime
          if (now.isBetween(pStart.subtract(6, 'minute'), pStart))
            status = 'soon'
        }
      })
      if (status === '') status = 'after'
      // console.log('live status', status)
      return {
        status,
        programStartTime
      }
    }

    // 参加病院リスト
    const eventHosptalsList = computed(() => {
      const res: any = []

      if (
        !Object.keys(props.event).length ||
        !Object.keys(props.hospitals).length ||
        !Object.keys(filterConditions).length ||
        props.isNoEntry
      )
        return res

      props.event.programs.forEach((program: any) => {
        const channelName = program.channel_name
        const channelDescription = program.channel_description
        const addedHospotalIds: string[] = []
        let currentHospital = 0

        program.item.forEach((item: any, index: never, array: any) => {
          if (
            !item.hospital_id ||
            !props.hospitals.hospital[item.hospital_id] ||
            currentHospital === item.hospital_id ||
            addedHospotalIds.includes(item.hospital_id) ||
            item.is_organization_online //WP"団体として追加する"にチェック済みは除く
          )
            return

          currentHospital = item.hospital_id
          addedHospotalIds.push(item.hospital_id)
          item.hospital = props.hospitals.hospital[item.hospital_id]

          let hEnd = ''
          const startTimes: string[] = []
          const endTimes: string[] = []
          array
            .filter((el: any) => el.hospital_id === item.hospital_id)
            .forEach((el: any) => {
              startTimes.push(`${el.start_hour}:${el.start_minute}`)
              hEnd = `${el.end_hour}:${el.end_minute}`
            })

          item.hospital_start = `${item.start_hour}:${item.start_minute}`
          item.hospital_end = hEnd
          item.channel_name = channelName
          item.channel_description = channelDescription

          const liveStatus = getLiveStatus(
            startTimes,
            currentTime.value,
            endTimes
          )
          item.liveStatus = liveStatus.status
          item.programStartTime = liveStatus.programStartTime

          res.push(item)
        })
      })

      return res
    })

    const filteredHosptalsList = computed(() => {
      const res: any = {}

      if (!eventHosptalsList.value.length) return res

      props.hospitals.area.forEach((area: string) => {
        res[area] = []
      })
      // 病院リストをliveStatusの値で並び変え
      const isLiveStatusHosptals = eventHosptalsList.value.filter(
        (hospitals: any) => hospitals.liveStatus === 'live'
      )
      const isSoonStatusHosptals = eventHosptalsList.value.filter(
        (hospitals: any) => hospitals.liveStatus === 'soon'
      )
      const isBeforeStatusHosptals = eventHosptalsList.value.filter(
        (hospitals: any) => hospitals.liveStatus === 'before'
      )
      const isAfterStatusHosptals = eventHosptalsList.value.filter(
        (hospitals: any) => hospitals.liveStatus === 'after'
      )
      const filteredHosptalsList = [
        ...isLiveStatusHosptals,
        ...isSoonStatusHosptals,
        ...isBeforeStatusHosptals,
        ...isAfterStatusHosptals
      ]

      filteredHosptalsList.forEach((item: any) => {
        // お気に入りフィルター
        if (state.isFilteredFavorite) {
          const favoriteIds = store.state.userInfo.favorite_history.map(
            (el: Record<'id' | 'hospital_id', number>) => {
              return el.hospital_id
            }
          )
          const isMatch = favoriteIds.includes(Number(item.hospital_id))
          if (!isMatch) return
        }
        // エリアフィルター
        if (filterConditions.area) {
          if (filterConditions.area.items.some((el: Checkbox) => el.checked)) {
            const isMatch = filterConditions.area.items
              .filter((el: Checkbox) => el.checked)
              .some((el: Checkbox) => el.label === item.hospital.area)
            if (!isMatch) return
          }
        }
        // 病院種別 フィルター
        if (filterConditions.hc) {
          if (filterConditions.hc.items.some((el: Checkbox) => el.checked)) {
            const isMatch = filterConditions.hc.items
              .filter((el: Checkbox) => el.checked)
              .some((el: Checkbox) => el.label === item.hospital.hospital_class)
            if (!isMatch) return
          }
        }
        // キーワード フィルター
        if (filterConditions.dc) {
          if (filterConditions.dc.items.some((el: Checkbox) => el.checked)) {
            const isMatch = filterConditions.dc.items
              .filter((el: Checkbox) => el.checked)
              .every((el: Checkbox) =>
                item.hospital.discerning_conditions.includes(el.label)
              )
            if (!isMatch) return
          }
        }

        if (!res[item.hospital.area]) res[item.hospital.area] = []

        res[item.hospital.area].push(item)
      })

      Object.keys(res).forEach((area: string) => {
        if (res[area].length === 0) delete res[area]
      })

      return res
    })

    const filterFavorite = async () => {
      const isLogin = await store.dispatch('checkLogin')
      if (!isLogin) return

      initializeFilterConditions()
      state.isFilteredFavorite = !state.isFilteredFavorite
    }

    const checkedRequestHospital: any = reactive([])

    const changeRequestMode = () => {
      state.isRequestMode = !state.isRequestMode
      checkedRequestHospital.splice(0)
    }

    const changeRequestCheckbox = (ev: { value: string; checked: boolean }) => {
      if (ev.checked) {
        checkedRequestHospital.push(ev.value)
      } else {
        checkedRequestHospital.splice(
          checkedRequestHospital.indexOf(ev.value),
          1
        )
      }
    }

    const toReqDocs = () => {
      const ids = checkedRequestHospital.join('_')
      const url = `/document_request/${ids}/input`

      router.push({ path: url })
    }

    const eventNotEntryModal = reactive({
      isShow: false,
      lead: '',
      text: '',
      btnLabel: '',
      btnLink: ''
    })

    const eventModalStatus = reactive({
      currentChannel: '',
      selectedProgramStart: '',
      isShow: false
    })

    const closeNotEntryModal = () => (eventNotEntryModal.isShow = false)
    const closeEventModal = () => (eventModalStatus.isShow = false)

    const eventConfirmModalState = reactive({
      isShow: false
    })
    const closeEventConfirmModal = () => (eventConfirmModalState.isShow = false)
    const openEventModal = () => {
      eventConfirmModalState.isShow = false
      eventModalStatus.isShow = true
    }

    const eventModalSelected = computed(() => {
      const res: any = {
        channel: {},
        program: {},
        hospital: {},
        recommend: [],
        liveStatus: {}
      }

      if (Object.keys(props.event).length && props.event.programs.length) {
        if (eventModalStatus.currentChannel) {
          res.channel = props.event.programs.find((channel: any) => {
            return channel.channel_name === eventModalStatus.currentChannel
          })
        } else {
          res.channel = props.event.programs[0]
        }
      }

      if (Object.keys(res.channel).length > 0) {
        const selectedProgram = res.channel.item.find((program: any) => {
          const programStartTime = `${program.start_hour}:${program.start_minute}`

          return programStartTime === eventModalStatus.selectedProgramStart
        })

        if (selectedProgram) {
          res.program = selectedProgram
          res.program.eventDate = props.eventDate
          res.liveStatus = getLiveStatus(
            [eventModalStatus.selectedProgramStart],
            currentTime.value,
            'end_hour' in selectedProgram &&
              selectedProgram.start_hour !== '' &&
              selectedProgram.start_minute !== '' &&
              selectedProgram.end_hour !== '' &&
              selectedProgram.end_minute !== ''
              ? [`${selectedProgram.end_hour}:${selectedProgram.end_minute}`]
              : []
          )
        }
      }

      if (Object.keys(res.program).length > 0 && res.program.hospital_id) {
        res.hospital = props.hospitals.hospital[res.program.hospital_id]
      }

      if (eventHosptalsList.value.length > 0) {
        eventHosptalsList.value.forEach((h: any) => {
          if (h.liveStatus === 'after') return
          if (res.hospital.hospital_id === h.hospital.hospital_id) return

          let count = 0

          if (res.hospital.area === h.area) count++

          if (
            res.hospital.discerning_conditions &&
            res.hospital.discerning_conditions.length > 0
          ) {
            res.hospital.discerning_conditions.forEach((condition: string) => {
              if (h.hospital.discerning_conditions.includes(condition)) count++
            })
          }

          if (count >= 2) {
            h.similarCount = count
            res.recommend.push(h)
          }
        })
        res.recommend.sort((a: any, b: any) => b.similarCount - a.similarCount)
      }

      return res
    })

    const eventModalSelectedHospitalNames = computed(() => {
      const hospilalNames = eventModalSelected.value.channel.item.map(
        (hospital: { hospital_name: string }) => hospital.hospital_name
      )
      const uniqueHospitalNames = getUniqeStringArray(hospilalNames)
      return uniqueHospitalNames
    })
    const eventModalSelectedHospitalIds = computed(() => {
      const hospilalNames = eventModalSelected.value.channel.item.map(
        (hospital: { hospital_id: string }) => hospital.hospital_id
      )
      const uniqueHospitalIds = getUniqeStringArray(hospilalNames)
      return uniqueHospitalIds
    })

    const viewLive = async (
      channelName: string,
      selectedProgramStart: string
    ) => {
      // ログインしていない場合
      if (!store.state.isLogin) {
        eventNotEntryModal.isShow = true
        eventNotEntryModal.lead =
          '会員限定コンテンツになります。<br>ご視聴には会員登録またはログインが必要です。'
        eventNotEntryModal.text =
          '会員登録後、イベントのご予約をお願いします。<br>※ご予約期限が設定されている場合がありますので、ご確認お願いいたします。'
        eventNotEntryModal.btnLabel = 'ログイン'
        eventNotEntryModal.btnLink = `/events/detail/${route.params.id}`
        // router.push(`/events/reserve/input/${route.params.id}`)
        return
      }

      if (props.isSubscribed) {
        if (props.event.programs_type !== 'b')
          eventModalStatus.currentChannel = channelName

        // WPカスタムフィールド 「同時チェックイン承諾案内を表示する」の場合は確認モーダル表示
        if (props.event.event_checkin_confirm) {
          eventConfirmModalState.isShow = true
        } else {
          eventModalStatus.isShow = true
        }
        eventModalStatus.selectedProgramStart = selectedProgramStart
      } else {
        // 予約されてない場合
        eventNotEntryModal.lead = 'イベントのご参加には事前予約が必要です。'
        const canSubscribe = () => {
          const now = dayjs(currentTime.value)
          // 開催期間（終了日時）が過ぎている
          if (now.isAfter(dayjs(props.event.term_end))) return false
          const entryStart = props.event.entry_start
            ? dayjs(props.event.entry_start)
            : null
          const entryEnd = props.event.entry_end
            ? dayjs(props.event.entry_end)
            : null
          // 受付期限が設定されていない
          if (!entryStart && !entryEnd) return true
          // 受付がどちらも設定されていて期限内
          if (entryStart && entryEnd && now.isBetween(entryStart, entryEnd))
            return true
          // 受付期限のどちらかのみ設定されていて期限内
          if (
            (entryStart && !entryEnd && now.isAfter(entryStart)) ||
            (!entryStart && entryEnd && now.isBefore(entryEnd))
          )
            return true
          // 受付期限のどちらかのみ設定されていて期限外
          if (
            (!entryStart && entryEnd && now.isAfter(entryEnd)) ||
            (entryStart && !entryEnd && now.isBefore(entryStart))
          )
            return false
          // それ以外
          return false
        }
        if (canSubscribe()) {
          eventNotEntryModal.isShow = true
          eventNotEntryModal.text =
            'ご予約いただいた後、各ブースのオンライン視聴が可能になります。'
          eventNotEntryModal.btnLabel = 'このイベントを予約する'
          eventNotEntryModal.btnLink = `/events/reserve/input/${route.params.id}`
        } else {
          const entryStart = props.event.entry_start
            ? props.getDateToWeek(props.event.entry_start)
            : ''
          const entryEnd = props.event.entry_end
            ? props.getDateToWeek(props.event.entry_end)
            : ''
          eventNotEntryModal.isShow = true
          eventNotEntryModal.text = `申し訳ございません。本イベントは、現在予約受付期間外です。<br>予約受付期間 : ${entryStart} 〜 ${entryEnd}`
          eventNotEntryModal.btnLabel = '他のイベントを探す'
          eventNotEntryModal.btnLink = '/events/list'
        }
      }
    }

    /*
     * チェックイン
     */
    const postCheckinInfo = axiosSetting('app', 'page')
    let isCheckedInEvent = false
    const checkedInHospitals: string[] = []
    interface ApplyCheckinParams {
      event_id: number
      holding_type: number
      data: {
        applicant_type: string
        hospital_id?: string
        program_id?: string
      }[]
    }
    const applyCheckin = (type: 'event' | 'hospital', hospital_id?: string) => {
      if (type === 'event' && isCheckedInEvent === true) return
      if (type === 'hospital' && !hospital_id) return
      if (
        type === 'hospital' &&
        hospital_id &&
        checkedInHospitals.includes(hospital_id)
      )
        return

      const params: ApplyCheckinParams = {
        event_id: props.event.id,
        holding_type: props.event.type === 'オンライン' ? 1 : 2,
        data: []
      }
      const applicant_typ = type === 'event' ? 'E' : 'EH'
      if (type === 'hospital' && hospital_id) {
        const hospitalIds = eventModalSelectedHospitalIds.value

        if (!hospitalIds) return
        const data = hospitalIds.map((id) => {
          return {
            applicant_type: applicant_typ,
            hospital_id: id,
            program_id: props.event.checkin_id.replace('_E_', '_EH_') + id
          }
        })
        params.data = data
      }
      postCheckinInfo
        .post(`/api/v1/check_in`, params)
        .then(() => {
          if (type === 'event') {
            isCheckedInEvent = true
          }
          if (type === 'hospital' && hospital_id) {
            checkedInHospitals.push(hospital_id)
          }
        })
        .catch((err) => {
          if (err.response.status === 500) {
            store.commit('showSystemError', 'modal')
          }
          if (err.response.status === 422) {
            const errors = err.response.data.err_msg

            Object.keys(errors).forEach((key: string) => {
              if (errors[key][0] != 'すでにチェックイン済みです') {
                alert(errors[key][0])
              }
            })
          }
          if (err.response.status === 504) {
            state.isCheckInError = true
          }
        })
    }

    return {
      eventDetailOnlineChannels,
      hasEventPrograms,
      hasHospitals,
      state,
      currentTime,
      eventHosptalsList,
      filterConditions,
      initializeFilterConditions,
      changeFilterConditions,
      filteredHosptalsList,
      viewLive,
      changeRequestMode,
      toReqDocs,
      changeRequestCheckbox,
      filterFavorite,
      closeNotEntryModal,
      closeEventModal,
      checkedRequestHospital,
      eventNotEntryModal,
      eventModalSelected,
      eventModalSelectedHospitalNames,
      eventModalStatus,
      eventConfirmModalState,
      applyCheckin,
      closeEventConfirmModal,
      openEventModal
    }
  }
})
